import React, { useEffect, useState } from 'react';
import { API_BASE, apiFetch, showAppConfirm } from './api';
import { mergeStyles, theme } from './theme';

const PERMISSIONS = [
  { key: 'adminUser', label: 'Admin-User' },
  { key: 'overview', label: 'Übersicht' },
  { key: 'planner', label: 'Tor-Planung' },
  { key: 'loadingBayPlanner', label: 'Ladezonen-Planung' },
  { key: 'license', label: 'Lizenz' },
  { key: 'general', label: 'Setup' },
  { key: 'gates', label: 'Tore' },
  { key: 'loadingBays', label: 'Ladezonen' },
  { key: 'vehicles', label: 'Fahrzeuge' },
  { key: 'drivers', label: 'Fahrer' },
  { key: 'vehicleClasses', label: 'Fahrzeugklassen' },
  { key: 'displayLayout', label: 'Display Layout' },
  { key: 'layoutEditor', label: 'Lagerlayout' },
  { key: 'warehouseLookup', label: 'Lager Lookup' },
  { key: 'websiteUsers', label: 'Website-User' },
];

const emptyForm = {
  username: '',
  password: '',
  permissions: PERMISSIONS.reduce((map, permission) => ({ ...map, [permission.key]: false }), {}),
};

const styles = {
  stack: { display: 'grid', gap: '20px' },
  card: { ...theme.glassCard, padding: '24px', color: 'var(--board-text-strong, #fff)' },
  title: { margin: '0 0 16px', color: 'var(--board-text-strong, #fff)', fontSize: '20px' },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '10px', alignItems: 'start' },
  input: { ...theme.input, width: '100%' },
  button: { ...theme.button, ...theme.primaryButton },
  tableWrap: { overflowX: 'auto' },
  table: theme.table,
  th: theme.th,
  td: theme.td,
  deleteBtn: { ...theme.button, ...theme.dangerButton, padding: '7px 12px', fontSize: '12px', marginRight: '6px' },
  editBtn: { ...theme.button, ...theme.warningButton, padding: '7px 12px', fontSize: '12px', marginRight: '6px' },
  saveBtn: { ...theme.button, ...theme.successButton, padding: '7px 12px', fontSize: '12px', marginRight: '6px' },
  mutedBtn: { ...theme.button, ...theme.mutedButton, padding: '7px 12px', fontSize: '12px', marginRight: '6px' },
  label: { display: 'block', marginBottom: '6px', fontSize: '12px', color: 'var(--board-text-muted, rgba(255,255,255,0.62))' },
  permissionWrap: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '7px 10px',
    borderRadius: '999px',
    background: 'var(--board-surface-soft, rgba(255,255,255,0.08))',
    border: '1px solid var(--board-border, rgba(255,255,255,0.15))',
    fontSize: '13px',
  },
  badge: {
    display: 'inline-block',
    marginRight: '5px',
    marginBottom: '4px',
    padding: '4px 8px',
    borderRadius: '999px',
    background: 'rgba(244,63,94,0.18)',
    border: '1px solid rgba(244,63,94,0.34)',
    color: 'var(--board-danger-text, #ffe4e6)',
    fontSize: '12px',
    fontWeight: 800,
  },
};

function normalizePermissions(value) {
  const source = value && typeof value === 'object' ? value : {};
  const permissions = PERMISSIONS.reduce((permissions, item) => {
    permissions[item.key] = Boolean(source[item.key]);
    return permissions;
  }, {});

  if (!permissions.loadingBayPlanner && source.loadingBayPlanner === undefined && permissions.planner) {
    permissions.loadingBayPlanner = true;
  }
  if (!permissions.loadingBays && source.loadingBays === undefined && permissions.gates) {
    permissions.loadingBays = true;
  }

  // Legacy compatibility: older users with Lagerlayout access should also see Lager Lookup.
  if (!permissions.warehouseLookup && source.warehouseLookup === undefined && permissions.layoutEditor) {
    permissions.warehouseLookup = true;
  }

  if (permissions.adminUser) {
    for (const item of PERMISSIONS) {
      permissions[item.key] = true;
    }
  }

  return permissions;
}

function buildAdminPermissions() {
  return PERMISSIONS.reduce((permissions, item) => {
    permissions[item.key] = true;
    return permissions;
  }, {});
}

function WebsiteUsers({ currentUser = null }) {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const firstUserMode = currentUser?.bootstrap || users.length === 0;

  const loadUsers = () => {
    apiFetch(`${API_BASE}/website-users`)
      .then(res => res.json())
      .then(data => setUsers(Array.isArray(data) ? data : []));
  };

  useEffect(() => { loadUsers(); }, []);
  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const togglePermission = (state, setter, permissionKey) => {
    const nextPermissions = {
      ...state.permissions,
      [permissionKey]: !state.permissions[permissionKey],
    };

    setter({
      ...state,
      permissions: normalizePermissions(nextPermissions),
    });
  };

  const saveUser = async (e) => {
    e.preventDefault();
    if (!form.username.trim() || !form.password) return;
    const payload = firstUserMode ? { ...form, permissions: buildAdminPermissions() } : form;

    await apiFetch(`${API_BASE}/website-users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    setForm(emptyForm);
    loadUsers();
  };

  const startEdit = (user) => {
    setEditId(user.id);
    setEditForm({
      username: user.username || '',
      password: '',
      permissions: normalizePermissions(user.permissions),
    });
  };

  const saveEdit = async (id) => {
    await apiFetch(`${API_BASE}/website-users/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(editForm),
    });

    setEditId(null);
    loadUsers();
  };

  const deleteUser = async (id) => {
    if (!await showAppConfirm('Website-User wirklich löschen?', {
      title: 'User löschen',
      confirmLabel: 'Löschen',
      danger: true,
    })) return;
    await apiFetch(`${API_BASE}/website-users/${id}`, { method: 'DELETE' });
    loadUsers();
  };

  const renderPermissions = (state, setter) => (
    <div>
      <span style={styles.label}>Berechtigungen</span>
      {firstUserMode && (
        <p style={{ margin: '0 0 8px', color: 'var(--board-text-soft, rgba(255,255,255,0.72))', fontSize: '13px' }}>
          Der erste User wird automatisch als Admin-User mit allen Rechten angelegt.
        </p>
      )}
      <div style={styles.permissionWrap}>
        {PERMISSIONS.map(permission => (
          <label key={permission.key} style={styles.chip}>
            <input
              type="checkbox"
              checked={firstUserMode ? true : Boolean(state.permissions[permission.key])}
              disabled={firstUserMode}
              onChange={() => togglePermission(state, setter, permission.key)}
            />
            {permission.label}
          </label>
        ))}
      </div>
    </div>
  );

  const isMobile = viewportWidth < 760;

  return (
    <div style={styles.stack}>
      <div style={styles.card}>
        <h2 style={styles.title}>Website-User anlegen</h2>
        <form style={mergeStyles(styles.formGrid, isMobile && { gridTemplateColumns: '1fr' })} onSubmit={saveUser}>
          <input
            style={styles.input}
            placeholder="Username *"
            value={form.username}
            onChange={e => setForm({ ...form, username: e.target.value })}
            required
          />
          <input
            style={styles.input}
            placeholder="Passwort *"
            type="password"
            value={form.password}
            onChange={e => setForm({ ...form, password: e.target.value })}
            required
          />
          {renderPermissions(form, setForm)}
          <button style={styles.button} type="submit">Hinzufügen</button>
        </form>
      </div>

      <div style={styles.card}>
        <h2 style={styles.title}>Website-User ({users.length})</h2>
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>ID</th>
                <th style={styles.th}>Username</th>
                <th style={styles.th}>Berechtigungen</th>
                <th style={styles.th}>Aktion</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => {
                const permissions = normalizePermissions(user.permissions);
                const enabledPermissions = PERMISSIONS.filter(item => permissions[item.key]);

                return (
                  <tr key={user.id}>
                    <td style={styles.td}>{user.id}</td>
                    {editId === user.id ? (
                      <>
                        <td style={styles.td}>
                          <input
                            style={styles.input}
                            value={editForm.username}
                            onChange={e => setEditForm({ ...editForm, username: e.target.value })}
                          />
                          <input
                            style={mergeStyles(styles.input, { marginTop: '8px' })}
                            placeholder="Neues Passwort"
                            type="password"
                            value={editForm.password}
                            onChange={e => setEditForm({ ...editForm, password: e.target.value })}
                          />
                        </td>
                        <td style={styles.td}>{renderPermissions(editForm, setEditForm)}</td>
                      </>
                    ) : (
                      <>
                        <td style={styles.td}><strong>{user.username}</strong></td>
                        <td style={styles.td}>
                          {enabledPermissions.length > 0
                            ? enabledPermissions.map(item => <span key={item.key} style={styles.badge}>{item.label}</span>)
                            : '-'}
                        </td>
                      </>
                    )}
                    <td style={styles.td}>
                      {editId === user.id ? (
                        <>
                          <button style={styles.saveBtn} onClick={() => saveEdit(user.id)}>Speichern</button>
                          <button style={styles.mutedBtn} onClick={() => setEditId(null)}>Abbrechen</button>
                        </>
                      ) : (
                        <>
                          <button style={styles.editBtn} onClick={() => startEdit(user)}>Bearbeiten</button>
                          <button style={styles.deleteBtn} onClick={() => deleteUser(user.id)}>Löschen</button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default WebsiteUsers;

